import { Box, Button, Chip, IconButton, Rating, Tooltip, Typography } from "@mui/material";
import { formatPrice } from "./helpers";
import { ACCENT, BADGE_STYLES, TEMPLATE_AVATAR_GRAD } from "./constants";
import TemplatePreviewCanvas from "../../Components/TemplatePreviewCanvas";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import GridViewIcon from "@mui/icons-material/GridView";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTheme } from "@mui/material/styles";


 const PreviewContent = ({ item }) => item.elements
    ? <TemplatePreviewCanvas elements={item.elements} resolution={item.resolution} />
    : item.imageUrl
      ? <img src={item.imageUrl} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      : <Box sx={{ width: "100%", height: "100%", backgroundColor: "#2a2a2a", display: "flex", alignItems: "center", justifyContent: "center" }}><GridViewIcon sx={{ color: "#555", fontSize: 32 }} /></Box>;


function ItemModal({ modalItem, items, bundle, purchased, toggleBundle, handleGet, onClose, onSwitchTemplate, onBundleAll }) {
  const [addedPairs, _setAddedPairs] = useState([]);
  const addedPairsRef = useRef([]);
  const theme = useTheme();
  const setAddedPairs = useCallback((updater) => {
    _setAddedPairs((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      addedPairsRef.current = next;
      return next;
    });
  }, []);

  const allRecs = useMemo(() => {
    if (modalItem.type !== "Template") return [];
    const sameRes = items.filter(
      (i) => i.type === "Template" && i.resolution === modalItem.resolution && i.id !== modalItem.id
    );
    return (sameRes.length >= 2 ? sameRes : items.filter((i) => i.type === "Template" && i.id !== modalItem.id));
  }, [items, modalItem]);
  const [slots, setSlots] = useState(() => [
    { rec: allRecs[0] ?? null, anim: "idle" },
    { rec: allRecs[1] ?? null, anim: "idle" },
  ]);
  const queueRef = useRef(2);

  const prevModalIdRef = useRef(modalItem.id);
  useLayoutEffect(() => {
    if (prevModalIdRef.current === modalItem.id) return;
    prevModalIdRef.current = modalItem.id;
    _setAddedPairs([]);
    addedPairsRef.current = [];
    queueRef.current = 2;
    setSlots([
      { rec: allRecs[0] ?? null, anim: "idle" },
      { rec: allRecs[1] ?? null, anim: "idle" },
    ]);
  }, [modalItem.id, allRecs]);

  const handleAdd = useCallback((slotIdx) => {
    setSlots((prev) => {
      const rec = prev[slotIdx]?.rec;
      if (!rec) return prev;
      setAddedPairs((p) => (p.includes(rec.id) ? p : [...p, rec.id]));
      return prev.map((s, i) => (i === slotIdx ? { ...s, anim: "exit" } : s));
    });

    setTimeout(() => {
      setSlots((prev) => {
        const displayedIds = prev.map((s) => s.rec?.id);
        const skipIds = new Set([...displayedIds, ...addedPairsRef.current]);
        let idx = queueRef.current;
        let next = null;
        for (let t = 0; t < allRecs.length; t++) {
          const candidate = allRecs[idx % allRecs.length];
          idx++;
          if (candidate && !skipIds.has(candidate.id)) { next = candidate; break; }
        }
        queueRef.current = idx;
        return prev.map((s, i) => (i === slotIdx ? { rec: next, anim: "enter-pre" } : s));
      });
      requestAnimationFrame(() => requestAnimationFrame(() => {
        setSlots((prev) => prev.map((s, i) => (i === slotIdx ? { ...s, anim: "enter" } : s)));
        setTimeout(() => {
          setSlots((prev) => prev.map((s, i) => (i === slotIdx ? { ...s, anim: "idle" } : s)));
        }, 380);
      }));
    }, 330);
  }, [allRecs, setAddedPairs]);

  const addedPairItems = addedPairs.map((id) => items.find((i) => i.id === id)).filter(Boolean);
  const totalPrice = modalItem.price + addedPairItems.reduce((sum, i) => sum + i.price, 0);

  const slotSx = (anim) => {
    if (anim === "exit")      return { transform: "translateY(-115%)", opacity: 0,   transition: "transform 0.3s cubic-bezier(0.4,0,0.6,1), opacity 0.25s" };
    if (anim === "enter-pre") return { transform: "translateY(115%)",  opacity: 0,   transition: "none" };
    if (anim === "enter")     return { transform: "translateY(0%)",    opacity: 1,   transition: "transform 0.36s cubic-bezier(0.2,0,0,1), opacity 0.3s" };
    return                           { transform: "translateY(0%)",    opacity: 1,   transition: "transform 0.3s, opacity 0.3s" };
  };


 
  return (
    <Box sx={{ display: "flex", flexDirection: {xs:"column",md:"row"}, alignItems: {xs:"stretch",md:"flex-start"}, gap: 2, width: {xs:"100%",md:"auto"} }}>
      <Box sx={{ display: "flex", flexDirection: {xs:"column",md:"row"}, borderRadius: {xs:1,md:3}, overflow: "hidden", backgroundColor: "#fff", boxShadow: "0 24px 60px rgba(0,0,0,0.32)", width: {xs:"100%",md:760}, minHeight: {xs:"auto",md:500}, maxHeight: {xs:"70vh",md:"88vh"} }}>
        <Box sx={{ width: {xs:"100%",md:"48%"}, height: {xs:200,md:"auto"}, flexShrink: 0, position: "relative", backgroundColor: "#e8e8e8", overflow: "hidden" }}>
          <PreviewContent item={modalItem} />
          {modalItem.type === "Template" && (
            <Box sx={{ position: "absolute", bottom: 10, left: 10, backgroundColor: "rgba(0,0,0,0.45)", borderRadius: 1, px: 1, py: 0.3 }}>
              <Typography sx={{ color: "#fff", fontSize: "0.6rem", fontWeight: 500 }}>
                {(modalItem.resolution || "296x128").replace("x", " × ")} · JSON / HTML
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", position: "relative", overflowY: "auto", minWidth: 0 }}>
          <IconButton onClick={onClose} size="small" sx={{ position: "absolute", top: 10, right: 10, color: "#999", zIndex: 1, "&:hover": { backgroundColor: "#f5f5f5" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{ p: {xs:2.2,md:3}, display: "flex", flexDirection: "column", gap: 1.2, flex: 1 }}>
            <Chip label={modalItem.type.toUpperCase()} size="small" sx={{ alignSelf: "flex-start", height: 22, fontSize: {xs:"0.65rem"}, fontWeight: 700, letterSpacing: 0.6, backgroundColor: "#fdf2f4", color: ACCENT, border: "1px solid rgba(90,28,47,0.18)" }} />

            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.25, color: "#1a1a1a", pr: 3  ,[theme.breakpoints.down("md")]: {fontSize:"1.05rem", }}}>
              {modalItem.name}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
              <Rating value={modalItem.rating} precision={0.5} readOnly size="small" sx={{ "& .MuiRating-iconFilled": { color: "#f59e0b" } }} />
              <Typography variant="body2" fontWeight={700}>{modalItem.rating.toFixed(1)}</Typography>
              <Typography variant="body2" color="text.secondary">({modalItem.reviews})</Typography>
              {modalItem.badge && BADGE_STYLES[modalItem.badge] && (
                <Chip label={modalItem.badge} size="small" sx={{ height: 18, fontSize: {xs:"0.65rem",md:"unset"}, fontWeight: 700, backgroundColor: BADGE_STYLES[modalItem.badge].bg, color: BADGE_STYLES[modalItem.badge].color }} />
              )}
            </Box>

            <Typography variant="body2" color="text.secondary">
              Category: <Box component="span" fontWeight={600} sx={{ color: "#1a1a1a" }}>{modalItem.category}</Box>
            </Typography>

            <Typography sx={{ fontSize: "0.63rem", fontWeight: 700, letterSpacing: 1.3, color: "#bbb", textTransform: "uppercase" }}>Description</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75, flex: 1 }}>
              {modalItem.description}
            </Typography>
            {addedPairItems.length > 0 && (
              <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: "#fdf6f7", border: "1px solid rgba(90,28,47,0.1)" }}>
                <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: 1.2, color: ACCENT, mb: 0.8, textTransform: "uppercase" }}>Added Pairings</Typography>
                {addedPairItems.map((pair) => (
                  <Box key={pair.id} sx={{ display: "flex", alignItems: "center", mb: 0.35, "&:last-child": { mb: 0 } }}>
                    <Typography variant="caption" sx={{ color: "#555", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pair.name}</Typography>
                    <Typography variant="caption" fontWeight={600} sx={{ color: "#1a1a1a", mx: 1, flexShrink: 0 }}>{formatPrice(pair.price)}</Typography>
                    <IconButton size="small" onClick={() => setAddedPairs((p) => p.filter((x) => x !== pair.id))} sx={{ p: 0.2, color: "#ccc", "&:hover": { color: ACCENT } }}>
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
            <Box sx={{ pt: 1.5, borderTop: "1px solid #f0f0f0", mt: "auto" }}>
              {purchased.includes(modalItem.id) ? (
                <Chip icon={<CheckCircleIcon sx={{ fontSize: "0.95rem !important", color: "#2e7d32 !important" }} />} label="Installed" size="small" color="success" variant="outlined" sx={{ height: 32, fontSize: "0.78rem" }} />
              ) : (
                <>
                  {addedPairItems.length > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                      <Typography variant="caption" color="text.secondary">{modalItem.name}</Typography>
                      <Typography variant="caption" fontWeight={600}>{formatPrice(modalItem.price)}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.8, mb: 1.5 }}>
                    <Typography fontWeight={800} sx={{ fontSize: {xs:"1.65rem",md:"2rem"}, color: totalPrice === 0 ? "#2e7d32" : "#1a1a1a", lineHeight: 1 }}>
                      {totalPrice === 0 ? "Free" : `₹${Number(totalPrice).toFixed(2)}`}
                    </Typography>
                    {addedPairItems.length > 0 && <Typography variant="caption" color="text.secondary">total</Typography>}
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {addedPairItems.length === 0 && (
                      <Tooltip title={bundle.includes(modalItem.id) ? "Remove from Cart" : "Add to Cart"} arrow>
                        <IconButton size="small" onClick={() => toggleBundle(modalItem.id)} sx={{ border: "1px solid", borderColor: bundle.includes(modalItem.id) ? ACCENT : "#ccc", color: bundle.includes(modalItem.id) ? ACCENT : "#aaa", borderRadius: 1.5, p: 0.75, flexShrink: 0 }}>
                          {bundle.includes(modalItem.id) ? <RemoveShoppingCartIcon sx={{ fontSize: 18 }} /> : <AddShoppingCartIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {addedPairItems.length > 0 ? (
                      <Button fullWidth disableElevation startIcon={<AddShoppingCartIcon />}
                        onClick={() => onBundleAll([modalItem.id, ...addedPairItems.map((i) => i.id)])}
                        sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.92rem", py: 1.4, borderRadius: 2, background: TEMPLATE_AVATAR_GRAD, color: "#fff", boxShadow: "none", "&:hover": { opacity: 0.9, boxShadow: "none" } }}
                      >
                        Bundle It · ₹{Number(totalPrice).toFixed(2)}
                      </Button>
                    ) : (
                      <Button fullWidth disableElevation startIcon={<ShoppingBagOutlinedIcon />} onClick={() => onBundleAll([modalItem.id, ...addedPairItems.map((i) => i.id)])}
                        sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.92rem", py: 1.4, borderRadius: 2, background: totalPrice === 0 ? "linear-gradient(90deg, #388e3c, #2e7d32)" : `linear-gradient(135deg, ${ACCENT} 0%, #6b1a2d 100%)`, color: "#fff", boxShadow: "none", "&:hover": { opacity: 0.9, boxShadow: "none" } }}
                      >
                        {totalPrice === 0 ? "Get Free" : `Buy ${modalItem.type} Now`}
                      </Button>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {allRecs.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: {xs:"row",md:"column"}, gap: 1.5, width: {xs:"100%",md:210}, flexShrink: 0, alignSelf: {xs:"flex-start",md:"center"}, overflowX: {xs:"auto",md:"visible"}, pb: {xs:0.5,md:0} }}>
          {slots.map((slot, slotIdx) => slot.rec && (
            <Box key={slotIdx} sx={{  flexShrink: 0, overflow: "hidden", borderRadius:{xs:2,md: 2.5} ,minHeight:150,[theme.breakpoints.up("md")]: {height:220 },[theme.breakpoints.down("md")]: {height:220 },[theme.breakpoints.down("md")]: {width:"48.5%"}}}>
              <Box
                onClick={() => onSwitchTemplate(slot.rec, [...addedPairsRef.current])}
                sx={{ height: "100%", position: "relative", borderRadius:{xs:2,md: 2.5}, overflow: "hidden", boxShadow: "0 10px 32px rgba(0,0,0,0.28)", cursor: "pointer", ...slotSx(slot.anim) }}
              >
                <PreviewContent item={slot.rec} />
                <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(to top, rgba(0,0,0,0.78) 0%, transparent 100%)", px: 1.2, pt: 3, pb: 1 }}>
                  <Typography sx={{ color: "#fff", fontSize:"0.68rem", fontWeight: 700, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {slot.rec.name}
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.62rem" }}>
                    {formatPrice(slot.rec.price)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleAdd(slotIdx); }}
                  sx={{ position: "absolute", top: 8, right: 8, width: 28, height: 28, p: 0, backgroundColor: "rgba(255,255,255,0.92)", color: ACCENT, boxShadow: "0 2px 8px rgba(0,0,0,0.3)", "&:hover": { backgroundColor: "#fff", transform: "scale(1.1)" }, transition: "transform 0.15s" }}
                >
                  <AddCircleOutlineIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.6rem", textAlign: "center", display: {xs:"none",md:"block"} }}>
            Tap + to add to order
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default ItemModal;